import { VisitorGroup } from 'graphql/generated/graphqlTypes';
import useTranslation from 'hooks/useTranslation/useTranslation';
import { toLower } from 'lodash';
import { useCallback, useMemo } from 'react';
import { parseEnumParam, parseParamToBooleanRecord, serializeEnum } from 'v2/filters/filterUrlParams';
import { hasEnabledFilter } from 'v2/filters/hasEnabledFilter';
import { createUserGroupFilterChips } from 'v2/filters/userGroups/createUserGroupFilterChips';
import { createUserGroupFilterSections } from 'v2/filters/userGroups/createUserGroupFilterSections';
import { filterVisitorGroups } from 'v2/filters/userGroups/filterVisitorGroups';
import { filterAccessDefault } from 'v2/filters/userGroups/userGroupFilterConstants';
import { FilterAccess, FilterOfficeMode, UserGroupFilterState } from 'v2/filters/userGroups/userGroupFilterTypes';
import { useFilterSearchParams } from 'v2/hooks/useFilterSearchParams/useFilterSearchParams';
import { useVisitorGroupSearch } from 'v2/hooks/useSearch/useVisitorGroupSearch';

interface Params {
  allVisitorGroups: VisitorGroup[];
}

type UserGroupFilterParam = 'search' | 'access' | 'officeMode';

const USER_GROUP_FILTER_PARAM_KEYS: UserGroupFilterParam[] = ['search', 'access', 'officeMode'];

const FILTER_OFFICE_MODE_VALUES = Object.values(FilterOfficeMode);

export const useUserGroupSearchAndFilters = ({ allVisitorGroups }: Params) => {
  const { translation } = useTranslation();

  const { searchParams, updateFilterParams, updateMultiSelectFilter } =
    useFilterSearchParams<UserGroupFilterParam>(USER_GROUP_FILTER_PARAM_KEYS);

  const searchParam = searchParams.get('search');
  const accessParam = searchParams.get('access');
  const officeModeParam = searchParams.get('officeMode');

  const filterStr = searchParam ?? '';
  const filterAccess = useMemo(() => parseParamToBooleanRecord(accessParam, filterAccessDefault), [accessParam]);
  const filterOfficeMode = useMemo(() => parseEnumParam(officeModeParam, FILTER_OFFICE_MODE_VALUES), [officeModeParam]);

  const setFilterStr = useCallback(
    (value: string) => updateFilterParams(() => ({ search: value === '' ? undefined : value })),
    [updateFilterParams]
  );

  const setFilterAccess = useCallback(
    (value: FilterAccess | ((current: FilterAccess) => FilterAccess)) =>
      updateMultiSelectFilter('access', filterAccessDefault, value),
    [updateMultiSelectFilter]
  );

  const setFilterOfficeMode = useCallback(
    (value: FilterOfficeMode) => updateFilterParams(() => ({ officeMode: serializeEnum(value) })),
    [updateFilterParams]
  );

  const clearAllFilters = useCallback(() => {
    updateFilterParams(() => ({ search: undefined, access: undefined, officeMode: undefined }));
  }, [updateFilterParams]);

  const isAnyFilterEnabled = useMemo(
    () => hasEnabledFilter(filterStr, filterAccess, filterOfficeMode),
    [filterStr, filterAccess, filterOfficeMode]
  );

  const filters: UserGroupFilterState = useMemo(
    () => ({
      filter: { filterAccess, filterOfficeMode },
      setters: { setFilterAccess, setFilterOfficeMode },
      clearAllFilters,
      isAnyFilterEnabled
    }),
    [filterAccess, filterOfficeMode, setFilterAccess, setFilterOfficeMode, clearAllFilters, isAnyFilterEnabled]
  );

  const searchedVisitorGroups = useVisitorGroupSearch({
    visitorGroups: allVisitorGroups,
    search: filterStr,
    translation
  });

  const filteredVisitorGroups = useMemo(
    () => filterVisitorGroups({ visitorGroups: searchedVisitorGroups, filterAccess, filterOfficeMode }),
    [searchedVisitorGroups, filterAccess, filterOfficeMode]
  );

  const countCaption = `${filteredVisitorGroups.length} ${toLower(translation.of)} ${allVisitorGroups.length} ${toLower(translation.user_groups)}`;

  const sections = useMemo(() => createUserGroupFilterSections({ translation, filters }), [translation, filters]);
  const chips = useMemo(() => createUserGroupFilterChips({ translation, filters }), [translation, filters]);

  return {
    filterStr,
    setFilterStr,
    filteredVisitorGroups,
    sections,
    chips,
    countCaption,
    isAnyFilterEnabled,
    clearAllFilters
  };
};
